// A static module graph with inspectable, recorded activations. Moving the
// episode slider selects a stored forward pass; no computation is simulated.
import { svgEl, tokenColor, valueRamp } from "../anim/helpers";
import { fetchJson } from "../data/load";

type Block = { shape: number[]; min: number; max: number; values: number[] };
type TraceStep = {
  t: number;
  direction: number;
  prev_action: number | null;
  acts: Record<string, Block>;
  action: number;
  oracle_label: number | null;
};
type Trace = {
  mission: { text: string; tokens: number[] };
  action_names: string[];
  outcome: string;
  steps_taken: number;
  mission_acts: { mission_vec: Block };
  ranges: Record<string, { min: number; max: number }>;
  steps: TraceStep[];
  source: Record<string, string | number>;
};

type NodeId =
  | "view"
  | "mission"
  | "direction"
  | "previous"
  | "fusion"
  | "memory"
  | "output";
const NODES: Record<
  NodeId,
  { title: string; key: string; description: string; operation: string }
> = {
  view: {
    title: "Encode the local view",
    key: "obs_vec",
    description:
      "Each cell’s codes become learned groups of numbers, called embeddings. Convolutions combine information from neighboring cells. A final layer produces a 64-number summary of the visible patch.",
    operation:
      "7 × 7 × 3 codes → 7 × 7 × 48 features → 5 × 5 × 32 → 3 × 3 × 32 → 288 → 64. Each convolution and the projection are followed by ReLU, which sets negative values to zero.",
  },
  mission: {
    title: "Encode the instruction",
    key: "mission_vec",
    description:
      "A GRU reads the instruction word by word and keeps a summary. The same summary is used throughout the episode, so the network has access to the instruction at every step.",
    operation:
      "Word IDs → 32 values per word → mission GRU → 64 values. This GRU runs over words; the policy GRU below runs over environment steps.",
  },
  direction: {
    title: "Encode the view direction",
    key: "dir_embed",
    description:
      "The simulator tells the agent which way it faces. A learned table converts that direction into eight numbers for the rest of the network to use.",
    operation:
      "Direction index (0–3) → one row of a 4 × 8 embedding table → 8 values.",
  },
  previous: {
    title: "Encode the previous action",
    key: "prev_action_embed",
    description:
      "This records the action that actually happened, including any forced change. On the first step, a START value tells the network there is no previous action.",
    operation:
      "Left, right, forward, or START → one row of a 4 × 8 embedding table → 8 values.",
  },
  fusion: {
    title: "Combine the four inputs",
    key: "fused",
    description:
      "The four groups of numbers are joined together. A learned layer mixes them, then ReLU sets negative outputs to zero. The resulting 128 values summarize the current inputs for the memory unit.",
    operation:
      "64 view + 64 mission + 8 direction + 8 previous action = 144 values → linear layer + ReLU → 128 values.",
  },
  memory: {
    title: "Update the policy memory",
    key: "hidden",
    description:
      "The policy GRU combines the current inputs with its previous memory. Learned gates control what to keep and what to update. The new memory is used to choose an action and carried into the next step.",
    operation:
      "128 current features + 128 previous memory values → 128 updated memory values. Memory starts at zero at the beginning of the episode.",
  },
  output: {
    title: "Score and choose an action",
    key: "logits",
    description:
      "A final layer turns the updated memory into three action scores, called logits. The agent chooses the highest score. Softmax converts the scores into the displayed probabilities; this recorded agent always takes the highest-scoring action.",
    operation:
      "128 memory values → linear layer → 3 logits → choose the highest-scoring action.",
  },
};
let instance = 0;

export async function mountNetworkFlows(): Promise<void> {
  for (const slot of document.querySelectorAll<HTMLElement>(
    "[data-network-flow]",
  )) {
    try {
      const trace = await fetchJson<Trace>(
        `media/journey/network/${slot.dataset.networkFlow ?? "full_r0"}.json`,
      );
      if (!trace.steps?.length) throw new Error("empty trace");
      createNetworkFlow(slot, trace);
    } catch (error) {
      console.error("Unable to load the recorded network values.", error);
      slot.innerHTML =
        '<p class="figure-note" role="status">The recorded network values could not be loaded. Reload the page to try again.</p>';
    }
  }
}

export function createNetworkFlow(
  mount: HTMLElement,
  trace: Trace,
): () => void {
  const uid = `network-${++instance}`;
  let selected: NodeId = "view";
  let stepIndex = 0;
  const ramp = valueRamp(
    [0, 1, 2, 3, 4].map((n) => tokenColor(`--value-${n}`)),
  );
  const node = (id: NodeId, title: string, detail: string, size: string) => `
    <button type="button" class="network-node" data-node="${id}" aria-pressed="${id === selected}" aria-controls="${uid}-detail">
      <span class="network-node__title">${title}</span><span class="network-node__description">${detail}</span><span class="network-node__shape">${size}</span>
    </button>`;
  mount.className = "network";
  mount.innerHTML = `
    <div class="network__heading"><p>Inputs → features → memory → action</p><span class="network__hint">Select a block to learn what it does</span></div>
    <div class="network-graph">
      <svg class="network-edges" aria-hidden="true"><defs><marker id="${uid}-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M 1 1 L 9 5 L 1 9" fill="none" stroke="#81929f" stroke-width="1.5" /></marker></defs><g class="network-wires"></g></svg>
      <div class="network-inputs">
        <div class="network-lane"><div class="network-input" data-input="view"><span class="network-input__label">Local view</span><span>7 × 7 cells<br>object · color · state</span></div>${node("view", "View encoder", "Embeddings → convolutions → projection", "64 values")}</div>
        <div class="network-lane"><div class="network-input" data-input="mission"><span class="network-input__label">Instruction</span><span data-mission></span></div>${node("mission", "Mission encoder", "Word embeddings → GRU", "64 values · reused each step")}</div>
        <div class="network-lane"><div class="network-input" data-input="direction"><span class="network-input__label">View direction</span><span data-direction></span></div>${node("direction", "Direction embedding", "Look up one of four directions", "8 values")}</div>
        <div class="network-lane"><div class="network-input" data-input="previous"><span class="network-input__label">Previous action</span><span data-previous></span></div>${node("previous", "Action embedding", "Executed action, or START", "8 values")}</div>
      </div>
      <div class="network-core">
        ${node("fusion", "1. Combine", "Concatenate → linear layer → ReLU", "144 inputs → 128 features")}
        <div class="network-memory">${node("memory", "2. Update memory", "Policy GRU", "128 features → 128 memory values")}<span class="network-memory__note">↻ Previous memory enters here.<br>Updated memory goes to the next step.</span></div>
        ${node("output", "3. Choose an action", "Linear head → largest score", "128 inputs → 3 action scores")}
      </div>
    </div>
    <p class="figure-note network__key">Each arrow passes a group of numbers to the next block. The loop carries memory from one step to the next.</p>
    <section class="network-recording" aria-label="Recorded episode">
      <div class="network-recording__intro"><h3>Inspect a recorded step</h3><p data-episode></p></div>
      <div class="network-controls"><button type="button" data-step="previous" aria-label="Previous recorded step">←</button><label for="${uid}-step">Step <output data-step-label></output></label><input id="${uid}-step" type="range" min="0" max="${trace.steps.length - 1}" value="0" step="1" aria-label="Recorded episode step"><button type="button" data-step="next" aria-label="Next recorded step">→</button></div>
      <p class="network-readout" aria-live="polite" data-readout></p>
    </section>
    <section class="network-detail" id="${uid}-detail" aria-label="Selected network block">
      <div class="network-detail__explanation"><h3 data-detail-title></h3><p data-description></p><details class="network-numbers"><summary>Dimensions and operations</summary><p class="network-operation" data-operation></p></details></div>
      <div class="network-detail__values"><p class="network-values-label" data-values-label></p><div class="network-values" role="img"></div><div class="network-scale"><span data-min></span><span class="network-scale__ramp" aria-hidden="true"></span><span data-max></span></div><p class="network-value-note">One square per output value. Blue is lower; orange is higher. Each block keeps the same color scale across steps.</p><details class="network-numbers"><summary>Show numeric values</summary><div data-numbers></div></details></div>
    </section>
    <div class="network-actions" aria-label="Recorded action scores"></div>
    <details class="network-source"><summary>About this recording</summary><p>This lesson uses values recorded from a trained model. Moving the slider shows another decision from the same episode; it does not retrain or run the model. This recording is separate from the study’s test results.</p><p data-source></p></details>`;
  const q = <T extends HTMLElement = HTMLElement>(selector: string) =>
    mount.querySelector<T>(selector)!;
  q("[data-mission]").textContent = `“${trace.mission.text}”`;
  q("[data-episode]").textContent =
    `Instruction: “${trace.mission.text}”. ${trace.outcome === "success" ? "Goal reached" : "Goal not reached"} after ${trace.steps_taken} steps. Use the slider to inspect each decision.`;
  q("[data-source]").textContent =
    `Checkpoint: ${trace.source.checkpoint}. Selection: ${trace.source.selection_rule}.`;
  const graph = q(".network-graph");
  const edges = mount.querySelector<SVGSVGElement>(".network-edges")!;
  const wires = edges.querySelector<SVGGElement>(".network-wires")!;
  const slider = q<HTMLInputElement>("input[type=range]");
  const previous = q<HTMLButtonElement>('[data-step="previous"]');
  const next = q<HTMLButtonElement>('[data-step="next"]');
  const directions = ["East", "South", "West", "North"];

  function drawEdges() {
    const rect = graph.getBoundingClientRect();
    if (!rect.width) return;
    edges.setAttribute("viewBox", `0 0 ${rect.width} ${rect.height}`);
    wires.replaceChildren();
    const box = (el: Element) => {
      const b = el.getBoundingClientRect();
      return { x: b.x - rect.x, y: b.y - rect.y, w: b.width, h: b.height };
    };
    const point = (
      el: Element,
      side: "right" | "left" | "top" | "bottom",
    ): [number, number] => {
      const b = box(el);
      return side === "right"
        ? [b.x + b.w, b.y + b.h / 2]
        : side === "left"
          ? [b.x, b.y + b.h / 2]
          : side === "top"
            ? [b.x + b.w / 2, b.y]
            : [b.x + b.w / 2, b.y + b.h];
    };
    const wire = (d: string, from: NodeId, to: NodeId, dashed = false) => {
      wires.append(
        svgEl("path", {
          d,
          fill: "none",
          "stroke-width": 1.6,
          class:
            selected === from || selected === to
              ? "network-edge is-selected"
              : "network-edge",
          "marker-end": `url(#${uid}-arrow)`,
          ...(dashed ? { "stroke-dasharray": "5 4" } : {}),
        }),
      );
    };
    const fusion = q('[data-node="fusion"]');
    const coreBelow =
      box(fusion).y > box(q(".network-inputs")).y + box(q(".network-inputs")).h;
    for (const id of ["view", "mission", "direction", "previous"] as const) {
      const button = q(`[data-node="${id}"]`);
      const [ix, iy] = point(q(`[data-input="${id}"]`), "right");
      const [bx, by] = point(button, "left");
      wire(`M ${ix} ${iy} L ${bx - 5} ${by}`, id, id);
      const [x, y] = point(button, "right");
      if (coreBelow) {
        const [fx, fy] = point(fusion, "top");
        const bus = rect.width - 8;
        wire(
          `M ${x} ${y} H ${bus} V ${fy - 18} H ${fx} V ${fy - 5}`,
          id,
          "fusion",
        );
      } else {
        const [fx, fy] = point(fusion, "left");
        const mid = x + (fx - x) * 0.48;
        wire(
          `M ${x} ${y} C ${mid} ${y}, ${mid} ${fy}, ${fx - 5} ${fy}`,
          id,
          "fusion",
        );
      }
    }
    for (const [from, to] of [
      ["fusion", "memory"],
      ["memory", "output"],
    ] as const) {
      const [x, y] = point(q(`[data-node="${from}"]`), "bottom");
      const [tx, ty] = point(q(`[data-node="${to}"]`), "top");
      wire(`M ${x} ${y} L ${tx} ${ty - 5}`, from, to);
    }
    const m = box(q('[data-node="memory"]'));
    wire(
      `M ${m.x + m.w} ${m.y + m.h * 0.7} H ${m.x + m.w + 22} V ${m.y + m.h * 0.3} H ${m.x + m.w + 5}`,
      "memory",
      "memory",
      true,
    );
  }

  function renderDetails() {
    const definition = NODES[selected];
    const block =
      selected === "mission"
        ? trace.mission_acts.mission_vec
        : trace.steps[stepIndex]!.acts[definition.key]!;
    const range =
      selected === "mission" ? block : (trace.ranges[definition.key] ?? block);
    q("[data-detail-title]").textContent = definition.title;
    q("[data-description]").textContent = definition.description;
    q("[data-operation]").textContent = definition.operation;
    q("[data-values-label]").textContent =
      `${block.values.length} output values · ${selected === "mission" ? "constant for this mission" : `step ${trace.steps[stepIndex]!.t}`}`;
    const values = q(".network-values");
    values.setAttribute(
      "aria-label",
      `${definition.title}: ${block.values.length} values, current minimum ${block.min}, maximum ${block.max}. Numeric values are available below.`,
    );
    values.replaceChildren(
      ...block.values.map((value, i) => {
        const cell = document.createElement("span");
        cell.style.background = ramp(
          (value - range.min) / (range.max - range.min || 1),
        );
        cell.title = `Feature ${i + 1}: ${value}`;
        return cell;
      }),
    );
    q("[data-min]").textContent = String(range.min);
    q("[data-max]").textContent = String(range.max);
    q("[data-numbers]").textContent = block.values
      .map((v, i) => `${i + 1}: ${v}`)
      .join(" · ");
    for (const button of mount.querySelectorAll<HTMLButtonElement>(
      "[data-node]",
    )) {
      button.setAttribute(
        "aria-pressed",
        String(button.dataset.node === selected),
      );
    }
    drawEdges();
  }

  function renderStep() {
    const step = trace.steps[stepIndex]!;
    slider.value = String(stepIndex);
    slider.setAttribute(
      "aria-valuetext",
      `Step ${step.t}, action ${trace.action_names[step.action]}`,
    );
    q("[data-step-label]").textContent = `${step.t} / ${trace.steps.at(-1)!.t}`;
    q("[data-direction]").textContent =
      `${directions[step.direction]} · index ${step.direction}`;
    q("[data-previous]").textContent =
      step.prev_action === null
        ? "START · no previous action"
        : trace.action_names[step.prev_action]!;
    q("[data-readout]").textContent =
      `Step ${step.t}: the policy chose ${trace.action_names[step.action]}. ${stepIndex === 0 ? "Memory starts at zero." : "Memory includes earlier steps in this episode."}`;
    previous.disabled = stepIndex === 0;
    next.disabled = stepIndex === trace.steps.length - 1;
    const actions = q(".network-actions");
    actions.replaceChildren(
      ...trace.action_names.map((name, i) => {
        const el = document.createElement("div");
        el.className = `network-action${step.action === i ? " is-chosen" : ""}`;
        const prob = step.acts.probs!.values[i]!;
        const title = document.createElement("b");
        title.textContent = `${name}${step.action === i ? " ✓" : ""}`;
        const value = document.createElement("span");
        value.textContent = `score ${step.acts.logits!.values[i]!.toFixed(2)} · ${(prob * 100).toFixed(1)}%`;
        const track = document.createElement("span");
        track.className = "network-action__bar";
        const fill = document.createElement("i");
        fill.style.width = `${prob * 100}%`;
        track.append(fill);
        el.append(title, value, track);
        return el;
      }),
    );
    renderDetails();
  }
  for (const button of mount.querySelectorAll<HTMLButtonElement>(
    "[data-node]",
  )) {
    button.addEventListener("click", () => {
      selected = button.dataset.node as NodeId;
      renderDetails();
    });
  }
  slider.addEventListener("input", () => {
    stepIndex = Number(slider.value);
    renderStep();
  });
  previous.addEventListener("click", () => {
    stepIndex = Math.max(0, stepIndex - 1);
    renderStep();
  });
  next.addEventListener("click", () => {
    stepIndex = Math.min(trace.steps.length - 1, stepIndex + 1);
    renderStep();
  });
  const observer =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(drawEdges)
      : null;
  observer?.observe(graph);
  renderStep();
  return () => observer?.disconnect();
}
